import React, { useState } from 'react';
import useInView from '../hooks/useInView';
import { LOANS, IMAGES } from '../data';

const SectionHeader = ({ label, title, subtitle, t, inView }) => (
  <div
    style={{
      textAlign: 'center',
      marginBottom: 72,
      opacity: inView ? 1 : 0,
      animation: inView ? 'fadeUp 0.7s cubic-bezier(.16,1,.3,1) forwards' : 'none',
    }}
  >
    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.gold, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16, fontWeight: 600 }}>
      — {label} —
    </div>
    <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 'clamp(2.2rem,4vw,3.5rem)', color: t.text, marginBottom: 20 }}>
      {title}
    </h2>
    {subtitle && (
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 17, color: t.textSub, maxWidth: 560, margin: '0 auto', lineHeight: 1.75 }}>
        {subtitle}
      </p>
    )}
  </div>
);

const LoanCard = ({ loan, index, hovered, onHover, onLeave, t, inView }) => (
  <div
    onMouseEnter={() => onHover(index)}
    onMouseLeave={onLeave}
    style={{
      background: hovered ? (document.body.classList.contains('dark') ? '#181818' : '#fff') : t.surface,
      border: `1px solid ${hovered ? t.gold : t.border}`,
      borderRadius: 20,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
      transform: hovered ? 'translateY(-8px)' : 'translateY(0)',
      boxShadow: hovered ? '0 32px 64px rgba(0,0,0,0.15)' : 'none',
      opacity: inView ? 1 : 0,
      animation: inView ? `fadeUp 0.6s ${0.08 * index}s cubic-bezier(.16,1,.3,1) forwards` : 'none',
    }}
  >
    {/* Image */}
    <div style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
      <img
        src={loan.img}
        alt={loan.title}
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transition: 'transform 0.6s cubic-bezier(.16,1,.3,1)',
          transform: hovered ? 'scale(1.08)' : 'scale(1)',
        }}
      />
      <div
        style={{
          position: 'absolute', inset: 0,
          background: hovered
            ? 'linear-gradient(to top,rgba(0,0,0,0.7) 0%,transparent 60%)'
            : 'linear-gradient(to top,rgba(0,0,0,0.4) 0%,transparent 50%)',
          transition: 'all 0.4s',
        }}
      />
      <div
        style={{
          position: 'absolute', top: 14, left: 14,
          background: 'rgba(201,162,39,0.95)',
          color: '#0a0a0a',
          fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700,
          padding: '4px 12px', borderRadius: 40, letterSpacing: 0.5,
        }}
      >
        {loan.sub}
      </div>
      <div
        style={{
          position: 'absolute', bottom: 14, left: 14, right: 14,
          display: 'flex', justifyContent: 'space-between',
          opacity: hovered ? 1 : 0, transition: 'opacity 0.3s',
        }}
      >
        <span style={{ color: '#fff', fontFamily: "'DM Sans',sans-serif", fontSize: 13 }}>
          Rate from <strong style={{ color: '#e8c040' }}>{loan.rate}</strong>
        </span>
        <span style={{ color: '#fff', fontFamily: "'DM Sans',sans-serif", fontSize: 13 }}>
          Up to <strong style={{ color: '#e8c040' }}>{loan.tenure}</strong>
        </span>
      </div>
    </div>

    {/* Text */}
    <div style={{ padding: 28 }}>
      <div style={{ fontSize: 26, marginBottom: 10 }}>{loan.icon}</div>
      <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 21, color: t.text, margin: '0 0 10px' }}>
        {loan.title}
      </h3>
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: t.textSub, lineHeight: 1.7, margin: 0 }}>
        {loan.desc}
      </p>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 18,
          color: t.gold, fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 600,
          transform: hovered ? 'translateX(4px)' : 'translateX(0)',
          transition: 'transform 0.3s',
        }}
      >
        Apply Now →
      </div>
    </div>
  </div>
);

const ServicesSection = ({ dark, t }) => {
  const [ref, inView] = useInView(0.1);
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section
      id="services"
      style={{
        padding: '120px 32px',
        background: t.bg2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background pattern */}
      <div
        style={{
          position: 'absolute', inset: 0, opacity: 0.025,
          backgroundImage: `repeating-linear-gradient(45deg,${t.gold} 0,${t.gold} 1px,transparent 0,transparent 50%)`,
          backgroundSize: '28px 28px',
        }}
      />

      <div ref={ref} style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <SectionHeader
          label="Our Services"
          title={<>Loan Solutions For <span className="gold-text">Every Need</span></>}
          subtitle="From your dream home to fueling your business, we connect you with the best loan rates across India's top lenders."
          t={t}
          inView={inView}
        />

        {/* Cards grid */}
        <div className="three-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 28 }}>
          {LOANS.map((loan, i) => (
            <LoanCard
              key={loan.title}
              loan={loan}
              index={i}
              hovered={hoveredCard === i}
              onHover={setHoveredCard}
              onLeave={() => setHoveredCard(null)}
              t={t}
              inView={inView}
            />
          ))}
        </div>

        {/* Investment Advisory Banner */}
        <div
          style={{
            marginTop: 48,
            borderRadius: 20,
            overflow: 'hidden',
            position: 'relative',
            opacity: inView ? 1 : 0,
            animation: inView ? 'fadeUp 0.7s 0.55s cubic-bezier(.16,1,.3,1) forwards' : 'none',
          }}
        >
          <img src={IMAGES.invest} alt="Investment advisory" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,rgba(201,162,39,0.93) 0%,rgba(232,192,64,0.88) 100%)' }} />
          <div
            style={{
              position: 'relative', zIndex: 2,
              padding: '52px 60px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24,
            }}
          >
            <div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: 'rgba(0,0,0,0.55)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                Additional Service
              </div>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 'clamp(1.5rem,3vw,2.2rem)', color: '#0a0a0a', margin: '0 0 10px' }}>
                💰 We Also Provide Advice On Money Investment
              </h3>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 16, color: 'rgba(0,0,0,0.65)', margin: 0, maxWidth: 480 }}>
                Expert guidance on smart investment strategies — SIP, mutual funds, FDs and more to grow your wealth confidently.
              </p>
            </div>
            <a
              href="#contact"
              style={{
                background: '#0a0a0a', color: '#c9a227',
                padding: '16px 36px', borderRadius: 10,
                fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 16,
                textDecoration: 'none', whiteSpace: 'nowrap',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)', transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; }}
            >
              Get Free Advice →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
